<template>
    <!--
        Details tab — variant B (inline-edit, Attio/Linear feel).
        Template rewritten into grouped inline key-value rows; the entire
        <script> (data / props / methods / computed / treeselects /
        CurrencyInputs / Add* modals) is preserved byte-for-byte so the
        form `name` attributes and submit contract are unchanged.
        Each row: label on the left, control on the right; native inputs
        are borderless until hover/focus for the inline-edit feel.
    -->
    <div class="ie-details">
        <!-- ===== General ===== -->
        <div class="ie-group">
            <div class="ie-group__title">General</div>

            <div class="ie-row">
                <label class="ie-row__label" for="name">Name <span class="ie-req">*</span></label>
                <div class="ie-row__control">
                    <input class="js-input-agency-name ie-input" v-model="agency_name" type="text" id="name" name="name" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="name_in_quickbooks">Name in QuickBooks</label>
                <div class="ie-row__control">
                    <input class="js-input-name-in-quickbooks ie-input" type="text" id="name_in_quickbooks"
                        name="name_in_quickbooks" v-model="name_in_quickbooks" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="company_type">Company type</label>
                <div class="ie-row__control">
                    <input type="hidden" name="company_type_id" v-model="company_type"
                        v-if="!company_type || (company_type && company_type.length <= 0)">
                    <treeselect v-model="company_type" id="company_type" :name="'company_type_id'"
                        placeholder="Choose type" :multiple="false" :options="company_types" :clearOnSelect="true" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="website">Website</label>
                <div class="ie-row__control">
                    <input v-model="website" type="text" id="website" name="website" class="ie-input" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="status">Status</label>
                <div class="ie-row__control">
                    <input type="hidden" name="status_id" v-model="status"
                        v-if="!status || (status && status.length <= 0)">
                    <treeselect :clearOnSelect="true" v-model="status" id="status" :name="'status_id'"
                        placeholder="Choose status" :multiple="false" :options="statusOptions" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="source">Source</label>
                <div class="ie-row__control">
                    <input type="hidden" name="source_id" v-model="source"
                        v-if="!source || (source && source.length <= 0)">
                    <treeselect :clearOnSelect="true" v-model="source" id="source" :name="'source_id'"
                        placeholder="Choose source" :multiple="false" :options="sourceOptions" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label">Created</label>
                <div class="ie-row__control ie-readonly tag-mono">{{ created }}</div>
            </div>

            <div class="ie-row" v-if="sale_contact">
                <label class="ie-row__label">Sales contact</label>
                <div class="ie-row__control ie-readonly">
                    <span v-if="sale_contact.name || sale_contact.lastname">{{ `${sale_contact.name} ${sale_contact.lastname}` }}</span>
                    <a :href="`mailto:${sale_contact.email}`" class="ie-link">Write an e-mail</a>
                </div>
            </div>
        </div>

        <!-- ===== Industry & niche ===== -->
        <div class="ie-group">
            <div class="ie-group__title">Industry &amp; niche</div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="industry">
                    Industry
                    <a class="ie-add" @click="toggleIndustryModal">+ Add new</a>
                </label>
                <div class="ie-row__control">
                    <input type="hidden" name="industries" v-model="industry"
                        v-if="!industry || (industry && industry.length <= 0)">
                    <treeselect id="industry" v-model="industry" :multiple="true" :valueConsistsOf="'ALL'"
                        name="industries[]" :options="industriesOptions" @select="handleChanged" @input="handleChanged"
                        @deselect="handleChanged" :clearOnSelect="true" />
                    <AddIndustryModal v-if="isOpenIndustryModal" :onClose="toggleIndustryModal" :onAdd="addIndustry"
                        :clearOnSelect="true" />
                </div>
            </div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="niche">
                    Niche
                    <a class="ie-add" @click="toggleNicheModal">+ Add new</a>
                </label>
                <div class="ie-row__control">
                    <input type="hidden" name="niches" v-model="niche" v-if="!niche || (niche && niche.length <= 0)">
                    <treeselect :clearOnSelect="true" id="niche" v-model="niche" :multiple="true"
                        :valueConsistsOf="'ALL'" name="niches[]" :options="nicheOptions" @select="handleChanged"
                        @input="handleChanged" @deselect="handleChanged" />
                    <AddNicheModal v-if="isOpenNicheModal" :onClose="toggleNicheModal" :onAdd="addNiche" />
                </div>
            </div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="industries_verticals">Industries / verticals</label>
                <div class="ie-row__control">
                    <textarea readonly v-model="industries_verticals" id="industries_verticals"
                        name="industries_verticals" class="ie-input ie-textarea"></textarea>
                </div>
            </div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="specialty">Specialty / niche offerings</label>
                <div class="ie-row__control">
                    <textarea readonly v-model="specialty" id="specialty" name="specialty"
                        class="ie-input ie-textarea"></textarea>
                </div>
            </div>
        </div>

        <!-- ===== Business ===== -->
        <div class="ie-group">
            <div class="ie-group__title">Business</div>

            <div class="ie-row">
                <label class="ie-row__label" for="number_of_employees">Number of employees</label>
                <div class="ie-row__control">
                    <input type="text" v-model="number_of_employees" name="number_of_employees"
                        id="number_of_employees" class="ie-input ie-mono" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="year_founded">Year founded</label>
                <div class="ie-row__control">
                    <input type="text" maxlength="4" name="year_founded" v-model="year_founded" id="year_founded"
                        @input="calculateFoundedYears" class="ie-input ie-mono" />
                    <span v-if="errors.year_founded" class="ie-error">{{ errors.year_founded }}</span>
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label">Years in business</label>
                <div class="ie-row__control ie-readonly tag-mono">{{ years_in_business }}</div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="office-type">Office type</label>
                <div class="ie-row__control">
                    <treeselect id="office-type" v-model="office_type" :multiple="false" name="office_type"
                        :options="data_office_types" @select="handleChanged" :clearOnSelect="false"
                        @input="handleChanged" @deselect="handleChanged" />
                </div>
            </div>
        </div>

        <!-- ===== Financials ===== -->
        <div class="ie-group">
            <div class="ie-group__title">Financials</div>

            <div class="ie-row">
                <label class="ie-row__label" for="min_contract_length">Min. contract length</label>
                <div class="ie-row__control">
                    <input type="text" v-model="minimum_contract_length" name="minimum_contract_length"
                        id="min_contract_length" class="ie-input" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="blended_hourly_rate">Blended hourly rate</label>
                <div class="ie-row__control">
                    <input hidden v-model="blended_hourly_rate" name="blended_hourly_rate" id="blended_hourly_rate" />
                    <CurrencyInput :id="'blended_hourly_rate_currency'" :name="'blended_hourly_rate_currency'"
                        v-model="blended_hourly_rate" :options="{
                            currency: 'USD',
                            hideCurrencySymbolOnFocus: false,
                            hideGroupingSeparatorOnFocus: false,
                            hideNegligibleDecimalDigitsOnFocus: false,
                        }" />
                </div>
            </div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="minimum_project_budget">Min. project budget</label>
                <div class="ie-row__control">
                    <input hidden v-model="minimum_project_budget" name="minimum_project_budget"
                        id="minimum_project_budget" />
                    <CurrencyInput :disabled="mpb_not_applicable" :id="'minimum_project_budget_currency'"
                        :name="'minimum_project_budget_currency'" v-model="minimum_project_budget" :options="{
                            currency: 'USD',
                            hideCurrencySymbolOnFocus: false,
                            hideGroupingSeparatorOnFocus: false,
                            hideNegligibleDecimalDigitsOnFocus: false,
                        }" />
                    <label class="ie-check" for="mpb_not_applicable">
                        <input v-model="mpb_not_applicable" type="checkbox" id="mpb_not_applicable"
                            name="mpb_not_applicable" />
                        Not applicable
                    </label>
                </div>
            </div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="minimum_monthly_retainer_fee">Min. monthly retainer</label>
                <div class="ie-row__control">
                    <input hidden v-model="minimum_monthly_retainer_fee" name="minimum_monthly_retainer_fee"
                        id="minimum_monthly_retainer_fee" />
                    <CurrencyInput :disabled="mmrf_not_applicable" :id="'minimum_monthly_retainer_fee_currency'"
                        :name="'minimum_monthly_retainer_fee_currency'" v-model="minimum_monthly_retainer_fee" :options="{
                            currency: 'USD',
                            hideCurrencySymbolOnFocus: false,
                            hideGroupingSeparatorOnFocus: false,
                            hideNegligibleDecimalDigitsOnFocus: false,
                        }" />
                    <label class="ie-check" for="mmrf_not_applicable">
                        <input v-model="mmrf_not_applicable" type="checkbox" id="mmrf_not_applicable"
                            name="mmrf_not_applicable" />
                        Not applicable
                    </label>
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="annual_gross_revenue_list">Gross revenue (range)</label>
                <div class="ie-row__control">
                    <input type="hidden" name="annual_gross_revenue_list" v-model="annual_gross_revenue"
                        v-if="!annual_gross_revenue || (annual_gross_revenue && annual_gross_revenue.length <= 0)">
                    <treeselect id="annual_gross_revenue_list" v-model="annual_gross_revenue"
                        name="annual_gross_revenue_list" :options="annual_gross_revenue_options" :clearOnSelect="true" />
                </div>
            </div>

            <div class="ie-row">
                <label class="ie-row__label" for="annual_gross_revenue">Gross revenue (exact)</label>
                <div class="ie-row__control">
                    <input type="text" hidden v-model="annual_gross_revenue_custom" name="annual_gross_revenue"
                        id="annual_gross_revenue" />
                    <CurrencyInput :id="'annual_gross_revenue_currency'" :name="'annual_gross_revenue_currency'"
                        v-model="annual_gross_revenue_custom" :options="{
                            currency: 'USD',
                            hideCurrencySymbolOnFocus: false,
                            hideGroupingSeparatorOnFocus: false,
                            hideNegligibleDecimalDigitsOnFocus: false,
                        }" />
                </div>
            </div>
        </div>

        <!-- ===== Audience ===== -->
        <div class="ie-group">
            <div class="ie-group__title">Audience</div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="client-size">Client size</label>
                <div class="ie-row__control">
                    <input type="hidden" name="client_sizes" v-model="client_size"
                        v-if="!client_size || (client_size && client_size.length <= 0)">
                    <treeselect id="client-size" v-model="client_size" :multiple="true" :valueConsistsOf="'ALL'"
                        name="client_sizes[]" :options="clientSizes" @select="handleChanged" :clearOnSelect="true"
                        @input="handleChanged" @deselect="handleChanged" />
                </div>
            </div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="lang">Additional languages</label>
                <div class="ie-row__control">
                    <input type="hidden" name="languages" v-model="lang" v-if="!lang || (lang && lang.length <= 0)">
                    <treeselect id="lang" v-model="lang" :multiple="true" :valueConsistsOf="'ALL'" name="languages[]"
                        :clearOnSelect="true" :options="languages" @select="handleChanged" @input="handleChanged"
                        @deselect="handleChanged" />
                </div>
            </div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="location">
                    Locations
                    <a class="ie-add" @click="toggleLocationModal">+ Add new</a>
                </label>
                <div class="ie-row__control">
                    <input type="hidden" name="locations" v-model="location"
                        v-if="!location || (location && location.length <= 0)">
                    <treeselect id="location" v-model="location" :multiple="true" :valueConsistsOf="'ALL'"
                        :clearOnSelect="true" name="locations[]" :options="locationOptions" />
                    <AddLocationModal v-if="isOpenLocationModal" :onClose="toggleLocationModal" :onAdd="addLocation"
                        :locationsOptions="locationOptions" :locationTypeOptions="locationTypeOptions" />
                    <label class="ie-check" for="remote_office">
                        <input v-model="remote_office" @change="onChangeRemoteOffice" type="checkbox" id="remote_office"
                            name="remote_office" />
                        Only remote
                    </label>
                </div>
            </div>

            <div class="ie-row ie-row--top">
                <label class="ie-row__label" for="social-media">Social media to tag</label>
                <div class="ie-row__control">
                    <input type="hidden" name="languages" v-model="socialMedia"
                        v-if="!socialMedia || (socialMedia && socialMedia.length <= 0)">
                    <treeselect id="social-media" v-model="socialMedia" :multiple="true" :valueConsistsOf="'ALL'"
                        name="social_media[]" :clearOnSelect="true" :options="social_media_list" @select="handleChanged"
                        @input="handleChanged" @deselect="handleChanged" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

import moment from "moment";
import { set_is_changed } from "../../helpers/_check_unsaved";
import AddIndustryModal from "../BusinessTab/modals/AddIndustry.vue";
import AddNicheModal from "../BusinessTab/modals/AddNiche.vue";
import AddLocationModal from "../BusinessTab/modals/AddLocation.vue";
import CurrencyInput from "../CurrencyInput";

export default {
    name: "detailsTab",
    props: [
        "agency_name",
        "agency_created",
        "agency_sale_contact",
        "agency_name_in_quickbooks",
        "agency_company_types",
        "agency_company_type",
        "agency_website",
        "agency_minimum_contract_length",
        "agency_year_founded",
        "agency_number_of_employees",
        "agency_source",
        "niches",
        "agency_niches",
        "industries",
        "agency_industries",
        "company",
        "agency_blended_hourly_rate",
        "agency_annual_gross_revenue",
        "agency_annual_gross_revenue_custom",
        "agency_min_project_budget",
        "agency_min_monthly_fee",
        "annualGrossRevenues",
        "annual_gross_revenue_options",
        "clientSizes",
        "agencyClientSizes",
        "locations",
        "location_types",
        "agencyLocations",
        "agency_remote_office",
        "languages",
        "social_media_list",
        "agencyLanguages",
        "agencySocialMedia",
        "sources",
        "agency_status",
        "statuses",
        "google_industries_verticals",
        "google_specialty",
        "industries_verticals_data",
        "specialty_data",
        "mpb_not_applicable_data",
        "mmrf_not_applicable_data",
        "agency_office_types",
        "google_office_description",
    ],
    components: {
        Treeselect,
        AddIndustryModal,
        AddNicheModal,
        AddLocationModal,
        CurrencyInput,
    },
    data() {
        return {
            name: "",
            created: "",
            sale_contact: "",
            name_in_quickbooks: "",
            company_type: null,
            website: "",
            industry: [],
            niche: [],
            status: null,
            number_of_employees: "",
            year_founded: "",
            years_in_business: 0,
            annual_gross_revenue: null,
            annual_gross_revenue_custom: 0,
            blended_hourly_rate: 0,
            minimum_contract_length: "",
            minimum_project_budget: "",
            minimum_monthly_retainer_fee: "",
            lang: null,
            socialMedia: null,
            client_size: null,
            location: null,
            source: null,
            errors: {
                year_founded: false,
            },
            industriesOptions: [],
            nicheOptions: [],
            statusOptions: [],
            locationOptions: [],
            locationTypeOptions: [],
            isOpenIndustryModal: false,
            isOpenNicheModal: false,
            isOpenLocationModal: false,

            is_uploaded: false,

            industries_verticals: "",
            specialty: "",
            remote_office: this.agency_remote_office,

            mpb_not_applicable: this.mpb_not_applicable_data ?? false,
            mmrf_not_applicable: this.mmrf_not_applicable_data ?? false,

            data_office_types: this.agency_office_types ?? [],
            office_type: null,
        };
    },

    watch: {
        mpb_not_applicable(val) {
            this.minimum_project_budget = 0;
        },
        mmrf_not_applicable(val) {
            this.minimum_monthly_retainer_fee = 0;
        },
    },
    computed: {
        company_types() {
            return this.agency_company_types.length
                ? this.agency_company_types.map((type) => {
                    return {
                        id: type.id,
                        label: type.name,
                    };
                })
                : [];
        },
        sourceOptions() {
            return this.sources.length
                ? this.sources.map((source) => {
                    return {
                        id: source.id,
                        label: source.name,
                    };
                })
                : [];
        },
    },
    mounted() {
        this.name = this.agency_name;
        this.created = moment(this.agency_created, "MM.DD.YYYY").format(
            "MM/DD/YYYY"
        );
        this.sale_contact = this.agency_sale_contact;
        this.name_in_quickbooks = this.agency_name_in_quickbooks;
        this.website = this.agency_website;
        this.industry = this.agency_industries;
        this.niche = this.agency_niches;
        this.source =
            this.agency_source ||
            this.sources.find((source) => source.is_default === 1).id ||
            null;
        this.status = this.agency_status || null;
        this.industriesOptions = this.industries;
        this.nicheOptions = this.niches;
        this.statusOptions = this.statuses || [];
        this.locationOptions = this.locations || [];
        this.locationTypeOptions = this.location_types || [];
        this.annual_gross_revenue_custom = this.agency_annual_gross_revenue_custom;
        this.blended_hourly_rate = this.agency_blended_hourly_rate;
        this.minimum_contract_length = this.agency_minimum_contract_length;
        this.year_founded = this.agency_year_founded;
        this.number_of_employees = this.agency_number_of_employees;

        this.minimum_project_budget = this.agency_min_project_budget;
        this.minimum_monthly_retainer_fee = this.agency_min_monthly_fee;
        const __company_type = this.agency_company_types.find(
            (type) => type.id === Number(this.agency_company_type)
        );
        if (__company_type) {
            this.company_type = __company_type.id;
        }
        else {
            this.company_type = 1;
        }

        if (this.company) {
            const __annual_gross_revenue =
                this.annual_gross_revenue_options.find(
                    (option) =>
                        option.id === Number(this.agency_annual_gross_revenue)
                );
            this.annual_gross_revenue = __annual_gross_revenue
                ? __annual_gross_revenue.id
                : null;
        }

        this.lang = this.agencyLanguages ? this.agencyLanguages : null;
        this.socialMedia = this.agencySocialMedia ? this.agencySocialMedia : null;
        (this.client_size = this.agencyClientSizes
            ? this.agencyClientSizes
            : null),
            (this.location = this.agencyLocations
                ? this.agencyLocations
                : null)

        if (
            typeof this.year_founded !== "string" &&
            (this.year_founded === 0 ||
            this.year_founded < 1900 ||
            this.year_founded > new Date().getFullYear())
        ) {
            this.year_founded = new Date().getFullYear();
        }

        this.industries_verticals = this.google_industries_verticals
            ? this.google_industries_verticals
            : this.industries_verticals_data ?? "";
        this.specialty = this.google_specialty
            ? this.google_specialty
            : this.specialty_data ?? "";

        setTimeout(() => {
            this.is_uploaded = true;
        }, 1000);

        this.office_type = this.data_office_types.find(office_t => office_t.label === this.google_office_description).id;

    },
    methods: {
        addIndustry(params) {
            axios
                .post("/industry/create", params)
                .then((res) => {
                    if (res.data) {
                        const new_data = res.data.industries.map((ind) => ({
                            id: ind.id,
                            label: ind.label,
                        }));

                        const new_item = res.data.industries.find(
                            (ind) => ind.label === params.name
                        );

                        this.industriesOptions = [...new_data];
                        this.industry.push(new_item.id);
                        this.isOpenIndustryModal = false;
                    }
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        addNiche(params) {
            axios
                .post("/niche/create", params)
                .then((res) => {
                    if (res.data) {
                        const new_data = res.data.niches.map((ind) => ({
                            id: ind.id,
                            label: ind.label,
                        }));

                        const new_item = res.data.niches.find(
                            (ind) => ind.label === params.name
                        );

                        this.nicheOptions = [...new_data];
                        this.niche.push(new_item.id);
                        this.isOpenNicheModal = false;
                    }
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        addLocation(params) {
            axios
                .post("/location/create", params)
                .then((res) => {
                    if (res.data) {
                        const new_data = {
                            id: res.data.location.id,
                            label: res.data.location.name
                        }

                        this.locationOptions.push(new_data);
                        this.location.push(new_data.id);
                        this.isOpenLocationModal = false;
                    }
                })
                .catch((err) => {
                    console.error(err);
                });

        },
        calculateFoundedYears() {
            const year = this.year_founded.toString();

            this.errors.year_founded = null;

            if (year === 0) {
                return;
            }

            if (year.length < 4) {
                this.errors.year_founded = "Please enter a full year";
            }

            if (year.length === 4 && Number(year) < 1900) {
                this.errors.year_founded = "The minimum year is 1900";
            }

            if (year.length === 4 && Number(year) > new Date().getFullYear()) {
                this.errors.year_founded = `The maximum year is ${new Date().getFullYear()}`;
            }

            if (year.length === 4) {
                const today = new Date();
                const founded_data = new Date(year, 0, 1);

                let today_year = today.getFullYear(),
                    today_month = today.getMonth() + 1,
                    today_date = today.getDate();

                let founded_year = founded_data.getFullYear(),
                    founded_month = founded_data.getMonth() + 1,
                    founded_date = founded_data.getDate();

                let fyear = Number.parseFloat(founded_year),
                    fmonth = Number.parseFloat(founded_month),
                    fdate = Number.parseFloat(founded_date),
                    tyear = Number.parseFloat(today_year),
                    tmonth = Number.parseFloat(today_month),
                    tdate = Number.parseFloat(today_date);

                if (Number(`${tyear - fyear}`) < 0) {
                    this.year_founded = today.getFullYear();
                    this.years_in_business = 0;
                    this.errors.year_founded = null;
                    return;
                }

                this.years_in_business = Number(`${tyear - fyear}`);
            }
        },
        handleChanged() {
            if (this.is_uploaded) set_is_changed();
        },
        toggleIndustryModal() {
            this.isOpenIndustryModal = !this.isOpenIndustryModal;
        },
        toggleNicheModal() {
            this.isOpenNicheModal = !this.isOpenNicheModal;
        },
        toggleLocationModal() {
            this.isOpenLocationModal = !this.isOpenLocationModal;
        },

        onChangeRemoteOffice(e) {
            if (e.target.checked) {
                this.remote_office = true;
            } else {
                this.remote_office = false;
            }
        },
    },
};
</script>

<style scoped>
/* Inline-edit (variant B) layout for the Details tab. Scoped so the
   .ie-* classes don't leak; >>> pierces into treeselect's internals to
   give it the borderless-until-hover inline feel. */
.ie-details {
    max-width: 760px;
}
.ie-group {
    margin-bottom: 18px;
}
.ie-group__title {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 0 6px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-faint);
}
.ie-group__title::after {
    content: "";
    flex: 1 1 auto;
    height: 1px;
    background: var(--border-faint);
}
.ie-row {
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 14px;
    align-items: center;
    padding: 8px;
    margin: 0 -8px;
    border-radius: 6px;
    min-height: 38px;
}
.ie-row + .ie-row {
    border-top: 1px solid var(--border-faint);
}
.ie-row--top {
    align-items: flex-start;
}
.ie-row:hover {
    background: var(--hover);
}
.ie-row__label {
    font-size: 12.5px;
    color: var(--text-muted);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    padding-top: 1px;
}
.ie-req {
    color: var(--tag-red);
}
.ie-add {
    font-size: 11px;
    color: var(--tag-red);
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
}
.ie-row__control {
    min-width: 0;
}
.ie-readonly {
    font-size: 13px;
    color: var(--text);
    padding: 5px 8px;
    display: flex;
    align-items: center;
    gap: 8px;
}
.ie-link {
    color: var(--tag-red);
    text-decoration: none;
    font-size: 12.5px;
}

/* Borderless native inputs — show a border on hover, accent on focus. */
.ie-input {
    width: 100%;
    border: 1px solid transparent;
    background: transparent;
    font-size: 13px;
    color: var(--text);
    padding: 5px 8px;
    border-radius: 6px;
    font-family: inherit;
    transition: border-color .12s, background .12s, box-shadow .12s;
}
.ie-input:hover {
    border-color: var(--border);
}
.ie-input:focus {
    border-color: var(--tag-red);
    background: var(--surface);
    box-shadow: var(--focus-ring);
    outline: none;
}
.ie-input::placeholder {
    color: var(--text-faint);
}
.ie-mono {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
}
.ie-textarea {
    min-height: 64px;
    resize: vertical;
    line-height: 1.45;
}
.ie-error {
    display: block;
    margin-top: 4px;
    padding-left: 8px;
    font-size: 11.5px;
    color: var(--tag-red);
}
.ie-check {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font-size: 12px;
    color: var(--text-muted);
    cursor: pointer;
}
.ie-check input {
    margin: 0;
}

/* Treeselect — strip the heavy box so it reads as inline text + chevron,
   border appears on hover (matches the native-input behaviour above). */
.ie-row__control >>> .vue-treeselect__control {
    border: 1px solid transparent;
    background: transparent;
    border-radius: 6px;
    min-height: 30px;
    transition: border-color .12s, background .12s;
}
.ie-row__control:hover >>> .vue-treeselect__control {
    border-color: var(--border);
}
.ie-row__control >>> .vue-treeselect--focused .vue-treeselect__control {
    border-color: var(--tag-red);
    background: var(--surface);
    box-shadow: var(--focus-ring);
}
.ie-row__control >>> .vue-treeselect__placeholder {
    color: var(--text-faint);
}
.ie-row__control >>> .vue-treeselect__multi-value-item {
    background: var(--tag-red-soft);
    border: 1px solid var(--tag-red-border);
    color: var(--text);
}
</style>
